
import { makeAutoObservable, runInAction } from 'mobx'
import { uniqBy } from 'lodash'
import { injectable, inject, decorate } from 'inversify'

// evertel
import { APIDataDocument } from '@evertel/types'
import { Api } from '@evertel/api'
import { DocumentStore, DocumentSchemaStore } from '../../store'


class DocumentSearchByRoomController {
    documents: APIDataDocument[] = []
    documentsLatestFetch: APIDataDocument[] = []
    documentSchemaIds: number[] = []
    documentsCount = 0
    roomId = 0
    searchTerm = ''
    page = 1
    limit = 100
    isLoading = false
    isInfiniteScroll = true

    constructor(
        private api: Api,
        private documentStore: DocumentStore,
        private documentSchemaStore: DocumentSchemaStore
    ) {
        makeAutoObservable(this)
    }

    init(roomId: number, isInfiniteScroll = true) {
        this.roomId = roomId
        this.isInfiniteScroll = isInfiniteScroll
        this.resetSearch()
    }

    setSearchTerm(value: string) {
        this.resetSearchMeta()
        this.searchTerm = value
    }

    setPage(pageNumber: number) {
        this.page = pageNumber
    }

    search = async () => {
        if (!this.roomId) return

        runInAction(() => {
            this.isLoading = true
        })

        // need to grab these here because of async issues that can happen after the first await
        const localFilter = this.filter
        const localSearchTerm = this.searchTerm
        const localRoomId = this.roomId
        const localPage = this.page


        let count = this.documentsCount
        let results = []

        this.documentsLatestFetch = []

        try {
        // grab the total count of docs in this room
            if (!this.documentsCount) {
                count = await this.fetchDocumentsCount(localFilter.where)
            }

            // stop from fetching if we already got them all
            if (count > this.documents?.length) {
                results = await this.api.Routes.Room.getDocumentsSearch(localRoomId, localFilter)
            }

            // Check if parameters have changed before updating state
            if ( this.searchTerm !== localSearchTerm || this.roomId !== localRoomId ) {
                // Parameters have changed; discard stale results
                return
            }

            runInAction(() => {
                this.documentsLatestFetch = results
                this.documents = (this.isInfiniteScroll) ? uniqBy([...this.documents, ...results], 'id') : results
                this.documentSchemaIds = this.documents.map(d => d.documentSchemaId)

                this.documentStore.update(results)

                // populate the schema store so DocumentIcon can render
                const schemas = results.map(d => d.documentSchema).filter(Boolean)
                this.documentSchemaStore.update(schemas)
            })
        } finally {
            runInAction(() => {
                this.isLoading = false
            })
        }
    }

    async fetchDocumentsCount(where = this.filter.where) {
        const res = await this.api.Routes.Room.getDocumentsSearchCount(this.roomId, where)

        runInAction(() => {
            this.documentsCount = res?.count ?? 0
        })

        return this.documentsCount
    }

    fetchNextDocuments() {
        this.page = this.page + 1
        this.search()
    }

    resetSearchMeta() {
        this.documents = []
        this.documentSchemaIds = []
        this.documentsCount = 0
        this.page = 1
    }

    resetSearch() {
        this.resetSearchMeta()
        this.searchTerm = ''
    }

    get filter() {
        return {
            where: {
                searchTerm: this.searchTerm
            },
            include: ['documentSchema'],
            order: 'updatedDate DESC',
            limit: this.limit,
            skip: (this.page - 1) * this.limit
        }
    }

}

decorate(injectable(), DocumentSearchByRoomController)
decorate(inject(Api), DocumentSearchByRoomController, 0)
decorate(inject(DocumentStore), DocumentSearchByRoomController, 1)
decorate(inject(DocumentSchemaStore), DocumentSearchByRoomController, 2)

export { DocumentSearchByRoomController }
